import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Card, DateField, Segmented, TextField, useSnackbar } from '@sauda/ui/kit';
import { api } from '../../lib/api.js';
import type { DealDetailView } from '../../lib/types.js';

type Status = 'not_yet' | 'done_by_broker' | 'done_by_owner';

/**
 * A plain checklist item: has the police verification of the tenant been done, and by whom. It only
 * records what the broker says. It claims nothing about filing and carries no legal text.
 */
export function PoliceCard({ deal, canEdit }: { deal: DealDetailView; canEdit: boolean }) {
  const { t } = useTranslation();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const saved = deal.facts?.police;
  const [status, setStatus] = useState<Status>(saved?.status ?? 'not_yet');
  const [date, setDate] = useState(saved?.date ?? '');
  const [note, setNote] = useState(saved?.note ?? '');
  useEffect(() => {
    setStatus(saved?.status ?? 'not_yet');
    setDate(saved?.date ?? '');
    setNote(saved?.note ?? '');
  }, [saved?.status, saved?.date, saved?.note]);
  const save = useMutation({
    mutationFn: () =>
      api(`/deals/${deal.id}/terms`, {
        method: 'PATCH',
        body: {
          facts: {
            police: {
              status,
              date: date === '' ? null : date,
              note: note.trim() === '' ? null : note.trim(),
            },
          },
        },
      }),
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: ['deal', deal.id] });
      snack({ text: t('deal.policeSaved') });
    },
    onError: () => snack({ text: t('deal.generic') }),
  });
  return (
    <Card>
      <h2 className="k-t-title-sm">{t('deal.policeTitle')}</h2>
      <p className="k-t-body-sm k-muted">{t('deal.policeHelp')}</p>
      <Segmented
        label={t('deal.policeTitle')}
        value={status}
        onChange={(v) => setStatus(v as Status)}
        options={[
          { value: 'not_yet', label: t('deal.police_not_yet') },
          { value: 'done_by_broker', label: t('deal.police_done_by_broker') },
          { value: 'done_by_owner', label: t('deal.police_done_by_owner') },
        ]}
      />
      {status === 'not_yet' ? null : (
        <DateField label={t('deal.policeDate')} value={date} onChange={setDate} />
      )}
      <TextField
        label={t('deal.policeNote')}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {canEdit ? (
        <Button variant="tonal" loading={save.isPending} onClick={() => save.mutate()}>
          {t('app.save')}
        </Button>
      ) : null}
    </Card>
  );
}
